import React, { useState, useEffect } from 'react'
import {
  Container,
  Row,
  Col,
  Breadcrumb,
  Card,
  Table,
  Badge,
  Button,
  Collapse,
  Spinner,
  Alert,
  Form,
} from 'react-bootstrap'
import { useParams, Link } from 'react-router-dom'
import useOrganization from 'hooks/use-organization'
import useGetPartnerIntegration from 'hooks/use-get-partner-integration'
import useGetPartnerWebhookDeliveries from 'hooks/use-get-partner-webhook-deliveries'
import { useGetOrganizationUsers } from 'hooks/use-get-organization-users'
import { useTogglePartnerIntegration } from 'hooks/use-toggle-partner-integration'
import { format } from 'date-fns'
import { WebhookDelivery } from 'interfaces/webhook-delivery'
import toast from 'react-hot-toast'

// Reusable webhook detail field component
interface WebhookDetailFieldProps {
  label: string
  value: string
  isLongText?: boolean
}

const WebhookDetailField: React.FC<WebhookDetailFieldProps> = ({
  label,
  value,
  isLongText = false,
}) => (
  <Form.Group className="mb-4">
    <Form.Label
      className="small fw-bold text-muted text-uppercase"
      style={{ letterSpacing: '0.5px' }}
    >
      {label}
    </Form.Label>
    {isLongText ? (
      <Form.Control
        as="textarea"
        value={value}
        readOnly
        className="text-dark"
        rows={2}
      />
    ) : (
      <Form.Control
        type="text"
        value={value}
        readOnly
        className="text-dark"
        style={{ wordBreak: 'break-all' }}
      />
    )}
  </Form.Group>
)

const PartnerIntegrationDetailPage = () => {
  const { id: integrationId, orgId } = useParams<{
    id: string
    orgId: string
  }>()
  const { data: orgData } = useOrganization(orgId)
  const org = orgData
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [afterId, setAfterId] = useState<number | undefined>(undefined)
  const [beforeId, setBeforeId] = useState<number | undefined>(undefined)
  const [prevIdHistory, setPrevIdHistory] = useState<number[]>([])
  const [copied, setCopied] = useState(false)
  const [previousWebhookIds, setPreviousWebhookIds] = useState<Set<number>>(
    new Set()
  )
  const [newWebhookIds, setNewWebhookIds] = useState<Set<number>>(new Set())
  const [isUsersCollapsed, setIsUsersCollapsed] = useState(true)

  const {
    data: integrationData,
    isLoading: integrationLoading,
    error: integrationError,
    refetch: refetchIntegration,
  } = useGetPartnerIntegration(integrationId)
  const {
    data: webhooksData,
    isLoading: webhooksLoading,
    isFetching: webhooksFetching,
    error: webhooksError,
    refetch: refetchWebhooks,
  } = useGetPartnerWebhookDeliveries(integrationId, {
    after_id: afterId,
    before_id: beforeId,
    per_page: 50,
  })
  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useGetOrganizationUsers(orgId)
  const toggleMutation = useTogglePartnerIntegration()

  const partnerIntegration = integrationData?.data
  const webhooks = webhooksData?.data || []
  const pagination = webhooksData?.pagination
  const allUserOrganizations = usersData?.data?.user_organizations || []
  // Only show active users with blue numbers
  const userOrganizations = allUserOrganizations.filter(
    (userOrg) => userOrg.active && !!userOrg.user?.blue_number
  )

  // Track new webhooks when data changes (only on refresh, not page change)
  useEffect(() => {
    if (webhooks.length > 0 && previousWebhookIds.size > 0) {
      const currentIds = new Set(webhooks.map((w) => w.id))
      const newIds = new Set<number>()

      currentIds.forEach((id) => {
        if (!previousWebhookIds.has(id)) {
          newIds.add(id)
        }
      })

      if (newIds.size > 0) {
        setNewWebhookIds((prevNewIds) => {
          const combined = new Set(prevNewIds)
          newIds.forEach((id) => combined.add(id))
          return combined
        })

        // Clear highlights after 30 seconds
        setTimeout(() => {
          setNewWebhookIds((prev) => {
            const updated = new Set(prev)
            newIds.forEach((id) => updated.delete(id))
            return updated
          })
        }, 30000)
      }
    }
  }, [webhooksData]) // Changed dependency to webhooksData to only trigger on data refresh

  const toggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const handleCopyToken = () => {
    if (partnerIntegration?.token) {
      navigator.clipboard.writeText(partnerIntegration.token).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
    }
  }

  const handleDocumentationClick = () => {
    window.open('https://apidocs.linqapp.com', '_blank')
  }

  const handleRefresh = () => {
    // Store current webhook IDs before refreshing
    if (webhooks.length > 0) {
      setPreviousWebhookIds(new Set(webhooks.map((w) => w.id)))
    }
    // Reset to first page on refresh
    setAfterId(undefined)
    setBeforeId(undefined)
    setPrevIdHistory([])
    refetchWebhooks()
  }

  const handleNextPage = () => {
    if (pagination?.next_id) {
      // Store current prev_id in history before navigating
      if (pagination.prev_id) {
        setPrevIdHistory((prev) => [...prev, pagination.prev_id!])
      }
      setAfterId(pagination.next_id)
      setBeforeId(undefined)
    }
  }

  const handlePrevPage = () => {
    if (prevIdHistory.length > 0) {
      const newHistory = [...prevIdHistory]
      const prevId = newHistory.pop()
      setPrevIdHistory(newHistory)
      setBeforeId(prevId)
      setAfterId(undefined)
    } else {
      // Go back to first page
      setAfterId(undefined)
      setBeforeId(undefined)
    }
  }

  const handleFirstPage = () => {
    setAfterId(undefined)
    setBeforeId(undefined)
    setPrevIdHistory([])
  }

  const handleToggleUser = (
    userOrgId: string,
    currentStatus: boolean | null | undefined,
    userName: string
  ) => {
    const newStatus = !currentStatus

    toggleMutation.mutate(
      {
        organizationId: orgId,
        userOrganizationId: userOrgId,
        enabled: newStatus,
      },
      {
        onSuccess: () => {
          toast.success(
            `Partner integration ${
              newStatus ? 'enabled' : 'disabled'
            } for ${userName}`
          )
          refetchUsers()
        },
        onError: (error: any) => {
          toast.error(
            error.message ||
              `Failed to ${
                newStatus ? 'enable' : 'disable'
              } partner integration`
          )
        },
      }
    )
  }

  const getStatusBadge = (status: string, httpStatus: number | null) => {
    const statusText = httpStatus != null ? ` (${httpStatus})` : ''
    if (status === 'success') {
      return <Badge variant="success">Success{statusText}</Badge>
    }
    if (status === 'failed') {
      return <Badge variant="danger">Failed{statusText}</Badge>
    }
    return <Badge variant="warning">Pending</Badge>
  }

  const getEventTypeBadge = (eventType: string) => {
    if (eventType.includes('sent')) {
      return <Badge variant="primary">{eventType}</Badge>
    }
    if (eventType.includes('received')) {
      return <Badge variant="info">{eventType}</Badge>
    }
    return <Badge variant="secondary">{eventType}</Badge>
  }

  return (
    <Container style={{ marginTop: '100px' }}>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/organizations">Organizations</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/organizations/${orgId}`}>
            {org?.name || 'Organization'}
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item active>
          Partner Integration
          {partnerIntegration?.name ? ` - ${partnerIntegration.name}` : ''}
        </Breadcrumb.Item>
      </Breadcrumb>

      {integrationError && (
        <Alert variant="danger" className="mb-4">
          Failed to load partner integration details: {integrationError.message}
        </Alert>
      )}

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Partner Integration Details</h4>
              {partnerIntegration && (
                <div>
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={handleCopyToken}
                    disabled={copied}
                    className="me-2"
                  >
                    <ion-icon
                      name={copied ? 'checkmark-outline' : 'copy-outline'}
                    />{' '}
                    {copied ? 'Copied!' : 'Copy Token'}
                  </Button>
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={handleDocumentationClick}
                  >
                    <ion-icon name="document-text-outline" /> API Docs
                  </Button>
                </div>
              )}
            </Card.Header>
            <Card.Body>
              {integrationLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : partnerIntegration ? (
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Name:</strong> {partnerIntegration.name}
                    </div>
                    <div className="mb-3">
                      <strong>Integration ID:</strong> {partnerIntegration.id}
                    </div>
                    <div className="mb-3">
                      <strong>Token:</strong>{' '}
                      <code
                        style={{
                          backgroundColor: '#f8f9fa',
                          padding: '2px 6px',
                          borderRadius: '3px',
                        }}
                      >
                        {partnerIntegration.token}
                      </code>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Created:</strong>{' '}
                      {format(
                        new Date(partnerIntegration.created_at),
                        'MMM d, yyyy h:mm a'
                      )}
                    </div>
                    <div className="mb-3">
                      <strong>Updated:</strong>{' '}
                      {format(
                        new Date(partnerIntegration.updated_at),
                        'MMM d, yyyy h:mm a'
                      )}
                    </div>
                    <div className="mb-3">
                      <strong>Webhook Subscriptions:</strong>{' '}
                      {partnerIntegration.webhook_subscriptions?.length || 0}
                    </div>
                  </Col>
                </Row>
              ) : (
                <Alert variant="warning">
                  No partner integration data available
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header
              onClick={() => setIsUsersCollapsed(!isUsersCollapsed)}
              style={{ cursor: 'pointer' }}
              className="d-flex justify-content-between align-items-center"
            >
              <h4 className="mb-0">Partner Integration Users</h4>
              <ion-icon
                name={
                  isUsersCollapsed
                    ? 'chevron-down-outline'
                    : 'chevron-up-outline'
                }
                style={{ fontSize: '24px', color: '#28a745' }}
              />
            </Card.Header>
            <Collapse in={!isUsersCollapsed}>
              <Card.Body>
                {usersLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading users...</span>
                    </Spinner>
                  </div>
                ) : usersError ? (
                  <Alert variant="danger">
                    Failed to load users:{' '}
                    {usersError instanceof Error
                      ? usersError.message
                      : 'Unknown error'}
                  </Alert>
                ) : userOrganizations.length === 0 ? (
                  <div className="text-center text-muted py-3">
                    No active users with blue numbers found in this
                    organization.
                  </div>
                ) : (
                  <>
                    <Alert variant="info" className="mb-3">
                      <small>
                        Enable partner integration for individual users. When
                        enabled, webhooks will be sent when that user's blue
                        number changes.
                      </small>
                    </Alert>
                    <div
                      style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                      }}
                    >
                      <Table striped hover responsive className="mb-0">
                        <thead
                          style={{
                            position: 'sticky',
                            top: 0,
                            backgroundColor: '#303030',
                            zIndex: 1,
                          }}
                        >
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Blue Number</th>
                            <th>Status</th>
                            <th>Enabled</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userOrganizations.map((userOrg) => {
                            const blueNumber = userOrg.user?.blue_number
                            const isEnabled =
                              userOrg.partner_integration_enabled === true

                            return (
                              <tr key={userOrg.id}>
                                <td>{userOrg.user?.name || '-'}</td>
                                <td>{userOrg.user?.email || '-'}</td>
                                <td>
                                  <span>{blueNumber}</span>
                                </td>
                                <td>
                                  {isEnabled ? (
                                    <Badge variant="success">Enabled</Badge>
                                  ) : (
                                    <Badge variant="secondary">Disabled</Badge>
                                  )}
                                </td>
                                <td>
                                  <Form.Check
                                    type="switch"
                                    id={`partner-integration-${userOrg.id}`}
                                    checked={isEnabled}
                                    onChange={() =>
                                      handleToggleUser(
                                        userOrg.id,
                                        isEnabled,
                                        userOrg.user?.name || 'User'
                                      )
                                    }
                                    disabled={toggleMutation.isLoading}
                                  />
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}
              </Card.Body>
            </Collapse>
          </Card>
        </Col>
      </Row>

      {partnerIntegration?.webhook_subscriptions &&
        partnerIntegration.webhook_subscriptions.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h4 className="mb-0">Webhook Subscriptions</h4>
                </Card.Header>
                <Card.Body>
                  {partnerIntegration.webhook_subscriptions.map(
                    (subscription) => (
                      <div
                        key={subscription.id}
                        className="border rounded p-3 mb-3"
                      >
                        <Row>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>URL:</strong>{' '}
                              <code style={{ fontSize: '12px' }}>
                                {subscription.webhook_url}
                              </code>
                            </div>
                            <div className="mb-2">
                              <strong>Events:</strong>{' '}
                              {subscription.events.map((event, idx) => (
                                <Badge
                                  key={idx}
                                  variant="info"
                                  className="me-1"
                                >
                                  {event}
                                </Badge>
                              ))}
                            </div>
                            <div className="mb-2">
                              <strong>Status:</strong>{' '}
                              <Badge
                                variant={
                                  subscription.active ? 'success' : 'secondary'
                                }
                              >
                                {subscription.active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Version:</strong> {subscription.version}
                            </div>
                            <div className="mb-2">
                              <strong>Delivery Attempts:</strong>{' '}
                              {subscription.delivery_attempts}
                            </div>
                            {subscription.last_delivered_at && (
                              <div className="mb-2">
                                <strong>Last Delivery:</strong>{' '}
                                {format(
                                  new Date(subscription.last_delivered_at),
                                  'MMM d, yyyy h:mm a'
                                )}
                              </div>
                            )}
                          </Col>
                        </Row>
                      </div>
                    )
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

      <Row>
        <Col>
          <div className="mb-3 d-flex justify-content-between align-items-start">
            <div>
              <h3 className="mb-1">Webhook Delivery Logs</h3>
              <p className="text-muted mb-0">
                Showing {webhooks.length} deliveries
                {pagination?.has_more && ' (more available)'}
                {newWebhookIds.size > 0 && (
                  <span className="text-success ms-2">
                    ({newWebhookIds.size} new)
                  </span>
                )}
              </p>
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleRefresh}
              disabled={webhooksLoading}
            >
              <ion-icon name="refresh-outline" /> Refresh
            </Button>
          </div>
          {webhooksError && (
            <Alert variant="danger" className="mb-3">
              Failed to load webhook logs: {webhooksError.message}
            </Alert>
          )}
          <Card>
            <Card.Body>
              {webhooksLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">
                      Loading webhook logs...
                    </span>
                  </Spinner>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      position: 'relative',
                      height: '500px',
                      overflow: 'auto',
                    }}
                  >
                    {webhooksFetching && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 10,
                        }}
                      >
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      </div>
                    )}
                    <div
                      style={{
                        opacity: webhooksFetching ? 0.5 : 1,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: webhooksFetching ? 'none' : 'auto',
                      }}
                    >
                      <Table striped hover>
                        <thead
                          style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 5,
                          }}
                        >
                          <tr>
                            <th
                              style={{
                                width: '40px',
                                backgroundColor: '#303030',
                              }}
                            />
                            <th style={{ backgroundColor: '#303030' }}>Time</th>
                            <th style={{ backgroundColor: '#303030' }}>
                              Event Type
                            </th>
                            <th style={{ backgroundColor: '#303030' }}>
                              Status
                            </th>
                            <th style={{ backgroundColor: '#303030' }}>
                              Duration
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {webhooks.length === 0 ? (
                            <tr>
                              <td
                                colSpan={7}
                                className="text-center py-4 text-muted"
                              >
                                No webhook deliveries found
                              </td>
                            </tr>
                          ) : (
                            webhooks.map((webhook) => (
                              <React.Fragment key={webhook.id}>
                                <tr
                                  style={{
                                    cursor: 'pointer',
                                    backgroundColor: newWebhookIds.has(
                                      webhook.id
                                    )
                                      ? '#28a745'
                                      : 'inherit',
                                    color: newWebhookIds.has(webhook.id)
                                      ? 'white'
                                      : 'inherit',
                                    transition:
                                      'background-color 0.5s ease, color 0.5s ease',
                                  }}
                                  onClick={() => toggleRow(webhook.id)}
                                >
                                  <td>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        toggleRow(webhook.id)
                                      }}
                                    >
                                      <ion-icon
                                        name={
                                          expandedRows.has(webhook.id)
                                            ? 'chevron-down-outline'
                                            : 'chevron-forward-outline'
                                        }
                                      />
                                    </Button>
                                  </td>
                                  <td>
                                    {format(
                                      new Date(webhook.delivered_at),
                                      'MMM d, h:mm:ss a'
                                    )}
                                  </td>
                                  <td>
                                    {getEventTypeBadge(webhook.event_type)}
                                  </td>
                                  <td>
                                    {getStatusBadge(
                                      webhook.status,
                                      webhook.http_status_code
                                    )}
                                  </td>
                                  <td>
                                    {webhook.duration != null
                                      ? `${webhook.duration}ms`
                                      : 'N/A'}
                                  </td>
                                </tr>
                                <tr>
                                  <td colSpan={5} style={{ padding: 0 }}>
                                    <Collapse in={expandedRows.has(webhook.id)}>
                                      <div>
                                        <Card className="m-3">
                                          <Card.Body className="p-5">
                                            <div className="row">
                                              <div className="col-md-6">
                                                <WebhookDetailField
                                                  label="Event ID"
                                                  value={
                                                    webhook.event_id || 'N/A'
                                                  }
                                                  isLongText={false}
                                                />
                                              </div>

                                              <div className="col-md-3">
                                                <WebhookDetailField
                                                  label="HTTP Status Code"
                                                  value={
                                                    webhook.http_status_code?.toString() ??
                                                    'N/A'
                                                  }
                                                />
                                              </div>

                                              <div className="col-md-3">
                                                <WebhookDetailField
                                                  label="Duration"
                                                  value={
                                                    webhook.duration != null
                                                      ? `${webhook.duration}ms`
                                                      : 'N/A'
                                                  }
                                                />
                                              </div>
                                            </div>

                                            <div className="row">
                                              <div className="col-md-12">
                                                <WebhookDetailField
                                                  label="Webhook URL"
                                                  value={
                                                    webhook.webhook_url || 'N/A'
                                                  }
                                                  isLongText={false}
                                                />
                                              </div>
                                            </div>

                                            <div className="row">
                                              <div className="col-md-6">
                                                <WebhookDetailField
                                                  label="Delivered At"
                                                  value={format(
                                                    new Date(
                                                      webhook.delivered_at
                                                    ),
                                                    'PPpp'
                                                  )}
                                                />
                                              </div>

                                              <div className="col-md-6">
                                                <WebhookDetailField
                                                  label="Updated At"
                                                  value={format(
                                                    new Date(
                                                      webhook.updated_at
                                                    ),
                                                    'PPpp'
                                                  )}
                                                />
                                              </div>
                                            </div>

                                            {webhook.error_message && (
                                              <div className="row">
                                                <div className="col-12">
                                                  <WebhookDetailField
                                                    label="Error Message"
                                                    value={
                                                      webhook.error_message ||
                                                      'N/A'
                                                    }
                                                    isLongText
                                                  />
                                                </div>
                                              </div>
                                            )}
                                          </Card.Body>
                                        </Card>
                                      </div>
                                    </Collapse>
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  {(pagination?.has_more ||
                    prevIdHistory.length > 0 ||
                    afterId) && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="text-muted" style={{ fontSize: '14px' }}>
                        Page {prevIdHistory.length + 1}
                        {pagination?.has_more && ' (more available)'}
                        {webhooksFetching && (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="ms-2"
                            role="status"
                          />
                        )}
                      </div>
                      <div className="d-inline-block">
                        <ul className="pagination m-0 p-0">
                          <li className="page-item">
                            <button
                              type="button"
                              className={`btn btn-outline-success rounded-0 me-1 ${
                                !afterId || webhooksFetching ? 'disabled' : ''
                              }`}
                              onClick={handleFirstPage}
                              disabled={!afterId || webhooksFetching}
                              style={{
                                cursor:
                                  !afterId || webhooksFetching
                                    ? 'not-allowed'
                                    : 'pointer',
                              }}
                            >
                              First
                            </button>
                          </li>
                          <li className="page-item">
                            <button
                              type="button"
                              className={`btn btn-outline-success rounded-0 me-1 ${
                                !afterId || webhooksFetching ? 'disabled' : ''
                              }`}
                              onClick={handlePrevPage}
                              disabled={!afterId || webhooksFetching}
                              style={{
                                cursor:
                                  !afterId || webhooksFetching
                                    ? 'not-allowed'
                                    : 'pointer',
                              }}
                            >
                              Prev
                            </button>
                          </li>

                          <li className="page-item">
                            <button
                              type="button"
                              className="btn btn-success rounded-0 me-1"
                              style={{ cursor: 'default' }}
                            >
                              {prevIdHistory.length + 1}
                            </button>
                          </li>

                          <li className="page-item">
                            <button
                              type="button"
                              className={`btn btn-outline-success rounded-0 me-1 ${
                                !pagination?.has_more || webhooksFetching
                                  ? 'disabled'
                                  : ''
                              }`}
                              onClick={handleNextPage}
                              disabled={
                                !pagination?.has_more || webhooksFetching
                              }
                              style={{
                                cursor:
                                  !pagination?.has_more || webhooksFetching
                                    ? 'not-allowed'
                                    : 'pointer',
                              }}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default PartnerIntegrationDetailPage
